import axios from 'axios'

// Helper function to safely get athlete data from localStorage
function getAthleteFromStorage () {
  const athleteData = localStorage.getItem('athlete')
  if (athleteData && athleteData !== 'undefined') {
    try {
      return JSON.parse(athleteData)
    } catch (e) {
      console.error('Error parsing athlete data:', e)
      localStorage.removeItem('athlete')
      return null
    }
  }
  return null
}

let $axios = axios.create({
  baseURL: '/api/',
  port: process.env.APP_PORT,
  timeout: 300000,
  headers: { 'Content-Type': 'application/json' }
})

// Track if we're currently refreshing the token to avoid multiple refresh attempts
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

// Request Interceptor
$axios.interceptors.request.use(function (config) {
  return config
})

// Response Interceptor to handle and log errors with automatic token refresh
$axios.interceptors.response.use(function (response) {
  return response
}, async function (error) {
  const originalRequest = error.config

  // Handle unauthorized responses with automatic token refresh
  if (error.response && error.response.status === 401) {
    // Check if this is a refresh token request itself - don't retry those
    if (originalRequest.url && originalRequest.url.includes('refresh-token')) {
      console.log('Refresh token itself is invalid, forcing re-login')
      // Refresh token is invalid, must re-login
      handleForceLogout()
      return Promise.reject(error.response)
    }

    // Get athlete from localStorage
    const athlete = getAthleteFromStorage()
    
    if (!athlete || !athlete.token || !athlete.token.refresh_token) {
      console.log('No athlete or refresh token found, forcing re-login')
      handleForceLogout()
      return Promise.reject(error.response)
    }

    // If we're already refreshing, queue this request
    if (isRefreshing) {
      console.log('Token refresh already in progress, queuing request')
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      }).then(token => {
        // Update the authorization header with the new token
        if (originalRequest.headers) {
          originalRequest.headers.authorization = token
        }
        return $axios(originalRequest)
      }).catch(err => {
        return Promise.reject(err)
      })
    }

    // Mark that we're refreshing and haven't retried this request yet
    originalRequest._retry = true
    isRefreshing = true

    console.log('Token expired, attempting to refresh token')

    try {
      // Call the refresh token endpoint
      const response = await $axios.put('athlete/auth/refresh-token', {
        athlete_id: athlete._id,
        refresh_token: athlete.token.refresh_token
      })

      if (response.data && response.data.token) {
        const newToken = response.data.token
        
        // Update athlete in localStorage
        athlete.token = newToken
        localStorage.setItem('athlete', JSON.stringify(athlete))
        
        console.log('Token refreshed successfully')
        
        // Update the authorization header for the original request
        if (originalRequest.headers) {
          originalRequest.headers.authorization = newToken.access_token
        }
        
        // Process queued requests with the new token
        processQueue(null, newToken.access_token)
        
        isRefreshing = false
        
        // Retry the original request with the new token
        return $axios(originalRequest)
      } else {
        throw new Error('Invalid refresh token response')
      }
    } catch (refreshError) {
      console.error('Failed to refresh token:', refreshError)
      processQueue(refreshError, null)
      isRefreshing = false
      handleForceLogout()
      return Promise.reject(refreshError)
    }
  }

  // Handle "Invalid token" responses (can be any status code)
  if (error.response && error.response.data && error.response.data.msg === 'Invalid token') {
    console.log('Invalid token detected, attempting to refresh')
    // Treat this the same as a 401 - try to refresh the token
    const athlete = getAthleteFromStorage()
    
    if (!athlete || !athlete.token || !athlete.token.refresh_token) {
      handleForceLogout()
      return Promise.reject(error.response)
    }

    // If we haven't retried yet, try to refresh
    if (!error.config._retry && !isRefreshing) {
      return $axios.interceptors.response.handlers[0].rejected(
        { ...error, response: { ...error.response, status: 401 } }
      )
    }
  }

  // Handle Error
  return Promise.reject(error.response)
})

// Helper function to handle forced logout
function handleForceLogout () {
  // Save current URL for redirect after successful login
  const currentUrl = window.location.pathname + window.location.search + window.location.hash
  localStorage.setItem('redirectUrl', currentUrl)

  // Remove athlete data
  localStorage.removeItem('athlete')

  // Update Vuex store to reflect logged out state
  if (window.$store) {
    window.$store.commit('setIsLoggedIn', false)
  }

  // Redirect to login page using Vue Router if available, otherwise fallback to window.location
  if (window.$router) {
    window.$router.push('/login')
  } else {
    window.location.href = '/login'
  }
}

export default {
  verifyRoundCounterEventCode (eventSlug, code) {
    let url = 'round-counter/' + eventSlug + '/auth'
    let payload = {
      'code': code
    }
    return $axios.post(
      url,
      payload
    )
  },
  countUp (eventSlug, bibNumber) {
    let url = 'round-counter/' + eventSlug
    let payload = {
      'delta': 0.5
    }

    if (typeof (bibNumber) !== 'undefined' && bibNumber !== '') {
      payload['bib_number'] = bibNumber
    }

    // TODO move this secret to const or config file
    return $axios.put(
      url,
      payload,
      {
        headers: {
          'x-ncb-secret-token': 'XNCBSecr3tHTW@F@RM@2024!'
        }
      }
    )
  },
  countDown (eventSlug, bibNumber) {
    let url = 'round-counter/' + eventSlug
    let payload = {
      'delta': -0.5
    }

    if (typeof (bibNumber) !== 'undefined' && bibNumber !== '') {
      payload['bib_number'] = bibNumber
    }

    // TODO move this secret to const or config file
    return $axios.put(
      url,
      payload,
      {
        headers: {
          'x-ncb-secret-token': 'XNCBSecr3tHTW@F@RM@2024!'
        }
      }
    )
  },

  loadCounters (eventSlug, bibNumber) {
    let url = 'round-counter/' + eventSlug

    if (typeof (bibNumber) !== 'undefined' && bibNumber !== '') {
      url += '?bib_number=' + bibNumber
    }
    return $axios.get(url)
      .then(resp => resp.data)
  },

  fetchResource () {
    return $axios.get('resource/xxx')
      .then(response => response.data)
  },

  fetchSecureResource () {
    return $axios.get('secure-resource/zzz')
      .then(response => response.data)
  },

  exchangeToken (client, code) {
    if (client === 'strava') {
      return $axios.get('strava/exchange-token/' + code)
        .then(response => response.data)
    }
    return null
  },

  sync (token, afterTs) {
    return {}
  },

  getAthChallenge (athlete, limit, offset) {
    let url = 'athlete/event/' + athlete._id
    if (limit && offset !== undefined) {
      url += '?limit=' + limit + '&offset=' + offset
    }
    return $axios.get(
      url,
      {
        headers: { authorization: athlete.token.access_token }
      }
    ).then(response => response.data)
  },

  refreshToken (athlete) {
    return $axios.put(
      'athlete/auth/refresh-token',
      {
        'athlete_id': athlete._id,
        'refresh_token': athlete.token.refresh_token
      }
    ).then(response => response.data)
  },

  join (athlete, slug, opt) {
    // TODO validate in this axios proxy
    let params = {
      'athlete_id': athlete._id,
      'token': athlete.token.access_token
    }
    if (opt['code']) {
      params['code'] = opt.code
    }
    if (opt['personal_target']) {
      params['personal_target'] = opt.personal_target
    }
    if (opt['personal_target_pace']) {
      params['personal_target_pace'] = opt.personal_target_pace
    }
    if (opt['personal_level']) {
      params['personal_level'] = opt.personal_level
    }
    if (opt['team_to_join']) {
      params['team_to_join'] = opt.team_to_join
    }

    return $axios.post('event/' + slug + '/join', params)
      .then(response => response.data)
  },

  getTopAthletes (offset, limit) {
    return $axios.get('athletes/top' + '?limit=' + limit + '&offset=' + offset).then(response => response.data)
  },

  getEvents (offset, limit) {
    return $axios.get('events' + '?limit=' + limit + '&offset=' + offset)
      .then(response => response.data)
  },

  getEvent (slug) {
    const athlete = getAthleteFromStorage()
    if (!athlete || !athlete.token || !athlete.token.access_token) {
      // Save current URL for redirect after successful login
      const currentUrl = window.location.pathname + window.location.search + window.location.hash
      localStorage.setItem('redirectUrl', currentUrl)

      // Remove any stale athlete data
      localStorage.removeItem('athlete')

      // Update Vuex store to reflect logged out state
      if (window.$store) {
        window.$store.commit('setIsLoggedIn', false)
      }

      // Redirect to login page
      if (window.$router) {
        window.$router.push('/login')
      } else {
        window.location.href = '/login'
      }

      const error = new Error('unauthorized')
      error.data = { msg: 'unauthorized' }
      error.status = 401
      return Promise.reject(error)
    }
    return $axios.get('event/' + slug, {
      headers: { authorization: athlete.token.access_token }
    }).then(response => response.data)
  },

  getEventStatsByTeam (slug, limit, offset) {
    return $axios.get('event/team-stats/' + slug + '?limit=' + limit + '&offset=' + offset)
      .then(response => response.data)
  },

  getEventVar (eventSlug) {
    const athlete = getAthleteFromStorage()
    if (!athlete || !athlete.token || !athlete.token.access_token) {
      // Save current URL for redirect after successful login
      const currentUrl = window.location.pathname + window.location.search + window.location.hash
      localStorage.setItem('redirectUrl', currentUrl)

      // Remove any stale athlete data
      localStorage.removeItem('athlete')

      // Update Vuex store to reflect logged out state
      if (window.$store) {
        window.$store.commit('setIsLoggedIn', false)
      }

      // Redirect to login page
      if (window.$router) {
        window.$router.push('/login')
      } else {
        window.location.href = '/login'
      }

      const error = new Error('unauthorized')
      error.data = { msg: 'unauthorized' }
      error.status = 401
      return Promise.reject(error)
    }
    return $axios.get('event/var/' + eventSlug, {
      headers: { authorization: athlete.token.access_token }
    }).then(response => response.data)
  },

  getEventPolylines (eventSlug) {
    const athlete = getAthleteFromStorage()
    if (!athlete || !athlete.token || !athlete.token.access_token) {
      // Save current URL for redirect after successful login
      const currentUrl = window.location.pathname + window.location.search + window.location.hash
      localStorage.setItem('redirectUrl', currentUrl)

      // Remove any stale athlete data
      localStorage.removeItem('athlete')

      // Update Vuex store to reflect logged out state
      if (window.$store) {
        window.$store.commit('setIsLoggedIn', false)
      }

      // Redirect to login page
      if (window.$router) {
        window.$router.push('/login')
      } else {
        window.location.href = '/login'
      }

      const error = new Error('unauthorized')
      error.data = { msg: 'unauthorized' }
      error.status = 401
      return Promise.reject(error)
    }
    return $axios.get('event/polylines/' + eventSlug, {
      headers: { authorization: athlete.token.access_token }
    }).then(response => response.data)
  },

  getEventAthlete (slug, limit, offset, cond) {
    const athlete = getAthleteFromStorage()
    if (!athlete || !athlete.token || !athlete.token.access_token) {
      // Save current URL for redirect after successful login
      const currentUrl = window.location.pathname + window.location.search + window.location.hash
      localStorage.setItem('redirectUrl', currentUrl)

      // Remove any stale athlete data
      localStorage.removeItem('athlete')

      // Update Vuex store to reflect logged out state
      if (window.$store) {
        window.$store.commit('setIsLoggedIn', false)
      }

      // Redirect to login page
      if (window.$router) {
        window.$router.push('/login')
      } else {
        window.location.href = '/login'
      }

      const error = new Error('unauthorized')
      error.data = { msg: 'unauthorized' }
      error.status = 401
      return Promise.reject(error)
    }

    let url = 'event/athlete/' + slug + '?limit=' + limit + '&offset=' + offset

    // Add filters to URL if they exist
    if (cond && cond.hasOwnProperty('athlete_sex')) {
      url += '&athlete_sex=' + cond['athlete_sex']
    }

    if (cond && cond.hasOwnProperty('team_slug')) {
      url += '&team_slug=' + cond['team_slug']
    }

    return $axios.get(url, {
      headers: { authorization: athlete.token.access_token }
    }).then(response => response.data)
  },
  searchAthleteInEvent (slug, searchAthleteInEvent, limit, offset) {
    const athlete = getAthleteFromStorage()
    if (!athlete || !athlete.token || !athlete.token.access_token) {
      // Save current URL for redirect after successful login
      const currentUrl = window.location.pathname + window.location.search + window.location.hash
      localStorage.setItem('redirectUrl', currentUrl)

      // Remove any stale athlete data
      localStorage.removeItem('athlete')

      // Update Vuex store to reflect logged out state
      if (window.$store) {
        window.$store.commit('setIsLoggedIn', false)
      }

      // Redirect to login page
      if (window.$router) {
        window.$router.push('/login')
      } else {
        window.location.href = '/login'
      }

      const error = new Error('unauthorized')
      error.data = { msg: 'unauthorized' }
      error.status = 401
      return Promise.reject(error)
    }
    return $axios.get('event/athlete/' + slug + '?limit=' + limit + '&offset=' + offset + '&athlete_name=' + searchAthleteInEvent, {
      headers: { authorization: athlete.token.access_token }
    }).then(response => response.data)
  },
  updateProfile (athlete) {
    athlete.autoAnalyseAndShare = athlete.autoAnalyseAndShare || false
    if (athlete.hasOwnProperty('auto_analyse_and_share')) {
      delete athlete.auto_analyse_and_share
    }

    return $axios.put('athlete/profile/' + athlete._id, {
      'token': athlete.token.access_token,
      'athlete': athlete
    })
      .then(response => response.data)
  },

  uploadAvatar (athleteId, formData, athlete) {
    return $axios.post('athlete/avatar/' + athleteId, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'token': athlete.token.access_token
      }
    }).then(response => response.data)
  },

  reportActivity (eventId, whoBeingReport, actLink, athlete) {
    return $axios.post('athlete/report-activity', {
      'event_id': eventId,
      'reporter_id': athlete._id,
      'who_being_report': whoBeingReport,
      'token': athlete.token.access_token,
      'act_link': actLink
    })
      .then(response => response.data)
  },
  inspectActivity (eventId, whoBeingInspected, actLink, athlete) {
    return $axios.post('athlete/inspect-activity', {
      'event_id': eventId,
      'inspector_id': athlete._id,
      'who_being_inspected': whoBeingInspected,
      'token': athlete.token.access_token,
      'act_link': actLink
    })
      .then(response => response.data)
  },
  getCert (eventID, athleteID) {
    return $axios.get('cert/' + eventID + '/' + athleteID).then(resp => resp.data)
  },

  getAthleteStats (athleteId, eventSlug) {
    return $axios.get('athlete/stats/' + athleteId + '/' + eventSlug)
      .then(response => response.data)
  },

  getAthleteProfile (athleteId, token) {
    return $axios.get('athlete/profile/' + athleteId, {
      headers: {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      }
    }).then(response => response.data)
  },

  getAthleteSubscription (athleteId, token) {
    return $axios.get('athlete/subscription/' + athleteId, {
      headers: {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      }
    }).then(response => response.data)
  },

  createSubscriptionVerification (athleteId, token, subscriptionData) {
    const headers = {
      'Content-Type': 'application/json'
    }
    // Only add Authorization header if token is provided
    if (token) {
      headers['Authorization'] = 'Bearer ' + token
    }
    return $axios.post('athlete/subscription/' + athleteId + '/verify', subscriptionData, {
      headers: headers
    }).then(response => response.data)
  },

  getAiHistory (athleteId, limit = 20, offset = 0) {
    let url = 'ai-gen/history/' + athleteId
    if (limit && offset !== undefined) {
      url += '?limit=' + limit + '&offset=' + offset
    }
    const athlete = getAthleteFromStorage()
    if (!athlete || !athlete.token || !athlete.token.access_token) {
      // Save current URL for redirect after successful login
      const currentUrl = window.location.pathname + window.location.search + window.location.hash
      localStorage.setItem('redirectUrl', currentUrl)

      // Remove any stale athlete data
      localStorage.removeItem('athlete')

      // Update Vuex store to reflect logged out state
      if (window.$store) {
        window.$store.commit('setIsLoggedIn', false)
      }

      // Redirect to login page
      if (window.$router) {
        window.$router.push('/login')
      } else {
        window.location.href = '/login'
      }

      return Promise.reject(new Error('No valid authentication token found'))
    }
    return $axios.get(url, {
      headers: {
        'Authorization': 'Bearer ' + athlete.token.access_token
      }
    }).then(response => response.data)
  },

  getEventInspection (eventSlug, limit = 20, offset = 0) {
    const athlete = getAthleteFromStorage()
    if (!athlete || !athlete.token || !athlete.token.access_token) {
      // Save current URL for redirect after successful login
      const currentUrl = window.location.pathname + window.location.search + window.location.hash
      localStorage.setItem('redirectUrl', currentUrl)

      // Remove any stale athlete data
      localStorage.removeItem('athlete')

      // Update Vuex store to reflect logged out state
      if (window.$store) {
        window.$store.commit('setIsLoggedIn', false)
      }

      // Redirect to login page
      if (window.$router) {
        window.$router.push('/login')
      } else {
        window.location.href = '/login'
      }

      const error = new Error('unauthorized')
      error.data = { msg: 'unauthorized' }
      error.status = 401
      return Promise.reject(error)
    }
    // Ensure limit and offset are valid numbers
    limit = parseInt(limit)
    if (isNaN(limit) || limit < 1) limit = 20
    offset = parseInt(offset)
    if (isNaN(offset) || offset < 0) offset = 0
    return $axios.get(`event/inspection/${eventSlug}?limit=${limit}&offset=${offset}`, {
      headers: { authorization: athlete.token.access_token }
    }).then(response => response.data)
  },

  generateAiContent (athleteId, activityId, athlete, type = 'image') {
    return $axios.post(`ai-gen/${athleteId}/${activityId}`, { type }, {
      headers: {
        'Authorization': `Bearer ${athlete.token.access_token}`,
        'Content-Type': 'application/json'
      }
    }).then(response => response.data)
  },

  generateAiContentWithSource (athleteId, activityId, athlete, type = 'image', sourceImageUrl) {
    return $axios.post(`ai-gen/${athleteId}/${activityId}`, {
      type,
      source_image_url: sourceImageUrl
    }, {
      headers: {
        'Authorization': `Bearer ${athlete.token.access_token}`,
        'Content-Type': 'application/json'
      }
    }).then(response => response.data)
  },

  generateAiContentWithFile (athleteId, activityId, athlete, type = 'image', formData) {
    return $axios.post(`ai-gen/${athleteId}/${activityId}`, formData, {
      headers: {
        'Authorization': `Bearer ${athlete.token.access_token}`,
        'Content-Type': 'multipart/form-data'
      }
    }).then(response => response.data)
  },

  getFeedActivities (offset, limit) {
    return $axios.get('feed/activities' + '?limit=' + limit + '&offset=' + offset)
      .then(response => response.data)
  },

  getAthleteActivities (athleteId, token, offset = 0, limit = 50, fromTs = null, toTs = null) {
    let url = `athlete/activities/${athleteId}?limit=${limit}&offset=${offset}`

    // Add timestamp filters if provided
    if (fromTs) {
      url += `&from_ts=${fromTs}`
    }
    if (toTs) {
      url += `&to_ts=${toTs}`
    }

    return $axios.get(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })
      .then(response => response.data)
  },

  syncAthleteActivities (athleteId, athlete) {
    return $axios.post(`athlete/activities/${athleteId}`, {}, {
      headers: {
        'Authorization': `Bearer ${athlete.token.access_token}`,
        'Content-Type': 'application/json'
      }
    }).then(response => response.data)
  },

  uploadSourceImage (athleteId, activityId, formData, athlete) {
    return $axios.post(`ai-gen/source/upload/${athleteId}/${activityId}`, formData, {
      headers: {
        'Authorization': `Bearer ${athlete.token.access_token}`,
        'Content-Type': 'multipart/form-data'
      }
    }).then(response => response.data)
  },

  updateAiContentPrivacy (athleteId, activityId, contentType, isPrivate, athlete) {
    return $axios.post(`ai-gen/privacy/${athleteId}/${activityId}`, {
      content_type: contentType,
      is_private: isPrivate
    }, {
      headers: {
        'Authorization': `Bearer ${athlete.token.access_token}`,
        'Content-Type': 'application/json'
      }
    }).then(response => response.data)
  },

  setAiContentPrivacy (athleteId, activityId, contentType, isPrivate) {
    const athlete = getAthleteFromStorage()
    if (!athlete || !athlete.token || !athlete.token.access_token) {
      // Save current URL for redirect after successful login
      const currentUrl = window.location.pathname + window.location.search + window.location.hash
      localStorage.setItem('redirectUrl', currentUrl)

      // Remove any stale athlete data
      localStorage.removeItem('athlete')

      // Update Vuex store to reflect logged out state
      if (window.$store) {
        window.$store.commit('setIsLoggedIn', false)
      }

      // Redirect to login page
      if (window.$router) {
        window.$router.push('/login')
      } else {
        window.location.href = '/login'
      }

      return Promise.reject(new Error('No valid authentication token found'))
    }

    return $axios.post(`ai-gen/privacy/${athleteId}/${activityId}`, {
      content_type: contentType,
      is_private: isPrivate
    }, {
      headers: {
        'Authorization': `Bearer ${athlete.token.access_token}`,
        'Content-Type': 'application/json'
      }
    }).then(response => response.data)
  },

  getAiTracklogStats (athleteId, activityId, athlete) {
    return $axios.get(`activity/stats/${athleteId}/${activityId}`, {
      headers: {
        'Authorization': `Bearer ${athlete.token.access_token}`,
        'Content-Type': 'application/json'
      }
    }).then(response => response.data)
  },

  shareToSocial (socialType, payload, athlete) {
    return $axios.post(`social/share/${socialType}`, payload, {
      headers: {
        'Authorization': `Bearer ${athlete.token.access_token}`,
        'Content-Type': 'application/json'
      }
    }).then(response => response.data)
  },

  /**
   * Get athlete activities in date range with automatic pagination
   * Fetches all activities between startDate and endDate
   * @param {number} athleteId - The athlete ID
   * @param {string} accessToken - Bearer access token
   * @param {string|Date} startDate - Start date for filtering
   * @param {string|Date} endDate - End date for filtering
   * @returns {Promise} Promise resolving to {data: Array, count: number}
   */
  async getAthleteActivitiesInRange (athleteId, accessToken, startDate, endDate) {
    const start = new Date(startDate)
    const end = new Date(endDate)

    console.log(`📊 Fetching activities for athlete ${athleteId}`)
    console.log(`📅 Date range: ${start.toLocaleDateString()} to ${end.toLocaleDateString()}`)

    let allActivities = []
    let offset = 0
    const limit = 100
    let hasMore = true

    try {
      // Fetch activities in batches until we have all in the date range
      while (hasMore) {
        const response = await this.getAthleteActivities(athleteId, accessToken, offset, limit)

        if (!response.data || response.data.length === 0) {
          hasMore = false
          break
        }

        // Filter activities by date range
        const filtered = response.data.filter(activity => {
          const actDate = new Date(activity.start_date_local)
          return actDate >= start && actDate <= end
        })

        allActivities = allActivities.concat(filtered)

        // If we got fewer activities than the limit, we've reached the end
        if (response.data.length < limit) {
          hasMore = false
        } else {
          // Check if the oldest activity in this batch is before our start date
          const oldestActivity = response.data[response.data.length - 1]
          const oldestDate = new Date(oldestActivity.start_date_local)

          // If oldest activity is before start date, no need to fetch more
          if (oldestDate < start) {
            hasMore = false
          } else {
            offset += limit
          }
        }
      }

      console.log(`✅ Found ${allActivities.length} activities in date range`)

      return {
        data: allActivities,
        count: allActivities.length
      }
    } catch (error) {
      console.error('❌ Error fetching activities in date range:', error)
      throw error
    }
  }

}
